/**
 * Bounded, chunking, retrying event queue.
 *
 *  - Capacity-capped (default 200). On overflow the OLDEST entry is dropped —
 *    the newest events are the most useful.
 *  - Flushes in chunks whose encoded request body is <= `maxChunkBytes`
 *    (default 60 KiB, safely under the 64 KiB `keepalive` limit) and
 *    <= `maxItemsPerRequest` items (the backend `ErrorBatch` accepts 50).
 *  - Network failure / 5xx / 408 / 429: the failed chunk and everything not yet
 *    sent are REQUEUED at the front (bounded by `maxAttempts` per entry).
 *    Other 4xx: the chunk is DROPPED (resending a rejected payload cannot succeed).
 *  - Identical events (same fingerprint) beyond `maxPerFingerprint` are not
 *    silently dropped: they are folded into a single pending entry whose
 *    `repeat` counter is incremented. If that entry has already been flushed,
 *    a fresh rollup entry carries on counting (bounded by
 *    `maxRollupsPerFingerprint` so a render loop cannot flood the sink).
 */

import type { SendFn } from "./types";

export const DEFAULT_QUEUE_CAPACITY = 200;
export const DEFAULT_MAX_CHUNK_BYTES = 60 * 1024;
export const DEFAULT_MAX_ITEMS_PER_REQUEST = 50;
export const DEFAULT_MAX_PER_FINGERPRINT = 20;
export const DEFAULT_MAX_ROLLUPS_PER_FINGERPRINT = 25;
export const DEFAULT_MAX_ATTEMPTS = 5;
const MAX_TRACKED_FINGERPRINTS = 500;

/** UTF-8 byte length of a string. */
export function byteLength(value: string): number {
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(value).length;
  let bytes = 0;
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4;
      i++;
    } else bytes += 3;
  }
  return bytes;
}

export interface QueueOptions<T> {
  capacity?: number;
  maxChunkBytes?: number;
  maxItemsPerRequest?: number;
  maxPerFingerprint?: number;
  maxRollupsPerFingerprint?: number;
  maxAttempts?: number;
  /** Fingerprint used to fold identical events; `undefined` disables folding for that item. */
  fingerprintOf: (item: T) => string | undefined;
  /** Return a copy of `item` carrying the given total `repeat` count. */
  withRepeat: (item: T, repeat: number) => T;
  /** Build the request body from already-JSON-encoded items. Default: `{"reports":[…]}`. */
  encode?: (encodedItems: string[]) => string;
  /** Serialize one item for the wire. Default: `JSON.stringify`. May throw (the item is then dropped). */
  serialize?: (item: T) => string;
}

export interface QueueStats {
  size: number;
  sent: number;
  /** Entries evicted because the queue was full, or dropped by the rollup / attempt caps. */
  dropped: number;
  /** Entries dropped because the server rejected the chunk with a non-retryable 4xx. */
  rejected: number;
  /** Entries folded into an existing entry's `repeat` counter. */
  merged: number;
  /** Number of times a chunk was put back for a later retry. */
  requeued: number;
}

export type PushResult = "queued" | "merged" | "dropped";

interface Entry<T> {
  item: T;
  json: string | null;
  repeat: number;
  attempts: number;
}

interface FingerprintState<T> {
  accepted: number;
  rollups: number;
  last: Entry<T> | null;
}

const defaultEncode = (encodedItems: string[]): string => `{"reports":[${encodedItems.join(",")}]}`;

/** Statuses worth retrying even though they are 4xx. */
function isRetryableStatus(status: number | undefined): boolean {
  if (status === undefined) return true; // network failure
  return status >= 500 || status === 408 || status === 429;
}

export class EventQueue<T> {
  private entries: Entry<T>[] = [];
  private readonly fingerprints = new Map<string, FingerprintState<T>>();
  private readonly options: Required<QueueOptions<T>>;
  private counters = { sent: 0, dropped: 0, rejected: 0, merged: 0, requeued: 0 };

  constructor(options: QueueOptions<T>) {
    this.options = {
      capacity: options.capacity ?? DEFAULT_QUEUE_CAPACITY,
      maxChunkBytes: options.maxChunkBytes ?? DEFAULT_MAX_CHUNK_BYTES,
      maxItemsPerRequest: options.maxItemsPerRequest ?? DEFAULT_MAX_ITEMS_PER_REQUEST,
      maxPerFingerprint: options.maxPerFingerprint ?? DEFAULT_MAX_PER_FINGERPRINT,
      maxRollupsPerFingerprint: options.maxRollupsPerFingerprint ?? DEFAULT_MAX_ROLLUPS_PER_FINGERPRINT,
      maxAttempts: options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
      fingerprintOf: options.fingerprintOf,
      withRepeat: options.withRepeat,
      encode: options.encode ?? defaultEncode,
      serialize: options.serialize ?? ((item: T) => JSON.stringify(item)),
    };
  }

  get size(): number {
    return this.entries.length;
  }

  stats(): QueueStats {
    return { size: this.entries.length, ...this.counters };
  }

  /** Items currently queued, oldest first. */
  snapshot(): T[] {
    return this.entries.map((e) => e.item);
  }

  clear(): void {
    this.entries = [];
    this.fingerprints.clear();
    this.counters = { sent: 0, dropped: 0, rejected: 0, merged: 0, requeued: 0 };
  }

  push(item: T): PushResult {
    const fp = this.options.fingerprintOf(item);
    let state: FingerprintState<T> | undefined;
    let isRollup = false;

    if (fp !== undefined) {
      state = this.fingerprints.get(fp);
      if (!state) {
        state = { accepted: 0, rollups: 0, last: null };
        this.fingerprints.set(fp, state);
        this.trimFingerprints();
      }
      if (state.accepted >= this.options.maxPerFingerprint) {
        const last = state.last;
        if (last && this.entries.includes(last)) {
          last.repeat += 1;
          last.item = this.options.withRepeat(last.item, last.repeat);
          last.json = null;
          this.counters.merged += 1;
          return "merged";
        }
        if (state.rollups >= this.options.maxRollupsPerFingerprint) {
          this.counters.dropped += 1;
          return "dropped";
        }
        state.rollups += 1;
        isRollup = true;
      }
    }

    const entry: Entry<T> = { item, json: null, repeat: 1, attempts: 0 };
    if (state) {
      if (!isRollup) state.accepted += 1;
      state.last = entry;
    }
    this.entries.push(entry);
    this.enforceCapacity();
    return "queued";
  }

  /**
   * Send everything currently queued, chunked. Returns the number of entries
   * accepted by the server (counting repeats as one entry each). Never throws.
   */
  async flush(send: SendFn): Promise<number> {
    if (this.entries.length === 0) return 0;
    const batch = this.entries.splice(0);
    const chunks = this.chunk(batch);
    let sent = 0;

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i] as Entry<T>[];
      const body = this.options.encode(chunk.map((e) => e.json as string));
      let status: number | undefined;
      let ok = false;
      try {
        const outcome = await send(body);
        ok = outcome.ok;
        if (!outcome.ok) status = outcome.status;
      } catch {
        ok = false;
        status = undefined;
      }

      if (ok) {
        sent += chunk.length;
        this.counters.sent += chunk.length;
        continue;
      }
      if (!isRetryableStatus(status)) {
        this.counters.rejected += chunk.length;
        continue;
      }
      // Retryable: put this chunk and everything not yet attempted back, stop here.
      this.requeue(chunks.slice(i).flat());
      break;
    }
    return sent;
  }

  // -------------------------------------------------------------------------

  private chunk(batch: Entry<T>[]): Entry<T>[][] {
    const overhead = byteLength(this.options.encode([]));
    const chunks: Entry<T>[][] = [];
    let current: Entry<T>[] = [];
    let currentBytes = overhead;

    for (const entry of batch) {
      if (entry.json === null) {
        try {
          entry.json = this.options.serialize(entry.item);
        } catch {
          this.counters.dropped += 1;
          continue;
        }
      }
      const size = byteLength(entry.json) + 1; // + comma
      const full =
        current.length >= this.options.maxItemsPerRequest || currentBytes + size > this.options.maxChunkBytes;
      if (current.length > 0 && full) {
        chunks.push(current);
        current = [];
        currentBytes = overhead;
      }
      current.push(entry);
      currentBytes += size;
    }
    if (current.length > 0) chunks.push(current);
    return chunks;
  }

  private requeue(entries: Entry<T>[]): void {
    const keep: Entry<T>[] = [];
    for (const entry of entries) {
      entry.attempts += 1;
      if (entry.attempts >= this.options.maxAttempts) this.counters.dropped += 1;
      else keep.push(entry);
    }
    this.counters.requeued += 1;
    // Older than anything pushed during the flush → front of the queue.
    this.entries = [...keep, ...this.entries];
    this.enforceCapacity();
  }

  private enforceCapacity(): void {
    const overflow = this.entries.length - this.options.capacity;
    if (overflow > 0) {
      this.entries.splice(0, overflow);
      this.counters.dropped += overflow;
    }
  }

  private trimFingerprints(): void {
    if (this.fingerprints.size <= MAX_TRACKED_FINGERPRINTS) return;
    const oldest = this.fingerprints.keys().next().value;
    if (oldest !== undefined) this.fingerprints.delete(oldest);
  }
}
